import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable, skipWhile, Subject} from 'rxjs';
import { throwError } from 'rxjs';
import {map, catchError, takeUntil, filter, tap, finalize} from 'rxjs/operators';

import {Identity, OtpDeliveryMethodStatus} from './identity.model';

import { LOCALE_ID, Inject } from '@angular/core';
import { NotificationService, NotificationType } from '../shared/notification.service';

import { ApiPaths } from '../../enums/api-paths';
import { HttpParams } from '@angular/common/http';
import { GenericErrorResponse, GenericResponse } from '../api-interfaces';
import { ConfigService } from '../config.service';
import { LogService } from '../shared/log.service';
import { ErrorService } from '../shared/errors.service';
import { User } from './user.model';
import { environment } from 'src/environments/environment';
import {AuthService} from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class IdentityService {

  private currentIdentity: Identity;

  // private loggedUser : User;
  currentIdentityChanged = new Subject<Identity>();

  currentViewChanged = new Subject<string>();

  private _isQrCodeModalVisible$ = new BehaviorSubject<boolean>(undefined);
  readonly isQrCodeModalVisible$ = this._isQrCodeModalVisible$.asObservable();
  get isQrCodeModalVisible(): boolean { return this._isQrCodeModalVisible$.getValue(); }

  private _qrCodeModalClosed$ = new BehaviorSubject<boolean>(false);
  readonly qrCodeModalClosed$ = this._qrCodeModalClosed$.asObservable();
  get qrCodeModalClosed(): boolean { return this._qrCodeModalClosed$.getValue(); }
  set qrCodeModalClosed(value: boolean) { this._qrCodeModalClosed$.next(value); }

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    @Inject(LOCALE_ID) public locale: string,
    private logger: LogService,
    private errorService: ErrorService,
    private authService: AuthService
  ) {
    // this.authService.loggedUser
    //   .pipe(take(1))
    //   .subscribe((loggedUser: User) => {
    //     this.loggedUser = loggedUser
    //   });

  }

  setCurrentIdentity(identity: Identity): void {
    this.currentIdentity = identity;
    this.currentIdentityChanged.next(this.currentIdentity);
  }

  setCurrentView(view: string): void {
    this.currentViewChanged.next(view);
  }

  getCurrentIdentity(): Identity {
    return this.currentIdentity;
  }

  // TODO: è una replica di quello in auth service. Si deve mettere in una parte comune.
  private _handleCmsRequest<T>(obs: Observable<any>, onOkResponse: any): Observable<Object> {
    return obs.pipe(
      map((response: GenericResponse) => {
        if (response.esito === 'OK') {
          onOkResponse(response);
        } else {
          this.notificationService.createNotification(
            NotificationType.ERROR,
            'Errore',
            this.errorService.getErrorBycode(response.result.code).message
          );
        }
        return response;
      }),
      catchError((err: any) => {
        const error: GenericErrorResponse = err.error;
        let errorMessage = this.errorService.getErrorBycode(error.result.error_code).message;
        const serializationErrors: any = error.result.serialization_errors;
        if (serializationErrors) {
          Object.keys(serializationErrors).forEach( (key: string) => {
            errorMessage = serializationErrors[key][0].message;
          });
        }
        this.notificationService.createNotification(
          NotificationType.ERROR,
          'Errore',
          errorMessage
        );

        return throwError(
          'Something bad happened; please try again later.');
      })
    );
  }

  activateIdentity(pk: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ActivateSpid}`;
    const payload = {
      pk,
      otp,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  hidePhoneNumber(phoneNumber: string): string {
    const phoneLength = phoneNumber.length;
    const center = phoneLength / 2;
    let hiddenPhoneNumbers = 0.5;
    if (environment.hiddenPhoneNumbers) {
      hiddenPhoneNumbers = environment.hiddenPhoneNumbers;
    }
    const notHidedNumbers = Math.floor(hiddenPhoneNumbers * phoneLength);
    return phoneNumber.replace(phoneNumber.slice(center - notHidedNumbers / 2, center + notHidedNumbers / 2), 'x'.repeat(notHidedNumbers));
  }

  requestOtp(phoneNumber?: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.SendOtp}`;
    const payload: any = {};
    if (phoneNumber) { payload.new_phone_number = phoneNumber; }
    return this._handleCmsRequest(this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.logger.log(response);
        const phone = response.result;
        const hidedPhoneNumber = this.hidePhoneNumber(phone);
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          `Attendi l'arrivo dell'OTP al numero ${hidedPhoneNumber}`
        );
      }
    );
  }

  sendOtp(phoneNumber?: string): any {
    this.requestOtp(phoneNumber).subscribe(
      (response: GenericResponse) => {
        if (response.esito === 'OK') {
          this.logger.log('Inviato otp');
        }
      },
      (err: Observable<never>) => { this.logger.log('Errore nell\'invio dell\'otp'); },
      () => { },
    );
  }

  changePassword(oldPassword: string, newPassword: string, currentUser: User, username?: string): any {
    let url, payload;
    url = `${ConfigService.settings.baseUrl}/${ApiPaths.SetPassword}`;
    payload = {
      username,
      current_pwd: oldPassword,
      new_pwd: newPassword,
    };
    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changePasswordWithOTP(oldPassword: string, newPassword: string, otp: string, username?: string): any {
    let url, payload;
    if (!username) {
      url = `${ConfigService.settings.baseUrl}/${ApiPaths.changePasswordWithOTP}`;
      payload = {
        current_pwd: oldPassword,
        new_pwd: newPassword,
        otp
      };
    }
    else {
      url = `${ConfigService.settings.baseUrl}/${ApiPaths.changePasswordWithOTP}`;
      payload = {
        username,
        current_pwd: oldPassword,
        new_pwd: newPassword,
        otp
      };
    }

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeEmail(identity: number, newEmail: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeEmail}`;
    const payload = {
      new_email: newEmail,
      otp,
      identity: identity.toString(),
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeEmailNotifications(): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeEmailNotifications}`;
    const payload = {

    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeAddress(addressType: string, addressName: string, addressNum: string, cap: string, city: string, province: string, country: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeAddress}`;
    const payload = {
      address: {
        address_type: addressType,
        address_name: addressName,
        address_num: addressNum,
        postal_code: cap,
        city,
        country,
        province
      },
      otp,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeCompany(addressType: string, addressName: string, addressNum: string, postalCode: string, city: string, province: string, country: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeCompany}`;
    const payload = {
      address_type: addressType,
      address_name: addressName,
      address_num: addressNum,
      postal_code: postalCode,
      city,
      country,
      province,
      otp,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeDigitalAddress(legalAddress: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeDigitalAddress}`;
    const payload = {
      pec: legalAddress,
      otp,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changeDocument(type: string, documentNumber: string, issueDate: string, documentExpirationDate: string, issuerType: string, issuerName: string, issuerCountry: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangeDocument}`;
    const payload = {
      document: {
        document_type: type,
        document_number: documentNumber,
        document_issue_date: issueDate,
        document_expiration_date: documentExpirationDate,
        document_issuer: issuerName,
        document_issuer_type: issuerType,
        document_issue_country: issuerCountry
      },
      otp,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  changePhoneNumber(identity: number, newNumber: string, otp: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ChangePhoneNumber}`;
    const payload = {
      new_phone_number: newNumber,
      otp,
      identity: identity.toString(),
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      },
    );
  }

  suspendIdentity(pk: string, emergencyCode: string, otpCode: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.SuspendIdentity}`;
    const payload = {
      pk,
      emergency_code: emergencyCode,
      otp: otpCode,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  reactivateIdentity(pk: string, emergencyCode: string, otpCode: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ReactivateIdentity}`;
    const payload = {
      pk: pk,
      emergency_code: emergencyCode,
      otp: otpCode,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  revokeIdentity(pk: string, emergencyCode: string, otpCode: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.RevokeIdentity}`;
    const payload = {
      pk: pk,
      emergency_code: emergencyCode,
      otp: otpCode,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta inviata con successo',
          ''
        );
      }
    );
  }

  createDraft(fiscalCode: string, email: string): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.CreateDraft}`;
    const payload = {
      fiscal_code: fiscalCode,
      email,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Bozza di richiesta creata con successo',
          ''
        );
      }
    );
  }

  cancelDraft(requestId: number): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.CancelDraft}/${requestId}`;

    return this._handleCmsRequest<GenericResponse>(
      this.http.delete<GenericResponse>(url, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Bozza di richiesta cancellata con successo',
          ''
        );
      }
    );
  }

  compileDraft(payload: any): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.CompileDraft}`;

    return this.http.post<GenericResponse>(url, payload, { withCredentials: true });
  }

  private generateQueryParametersEidup(pageIndex: number, pageSize: number, sortField: string, sortOrder: string, searchField: string, searchValue: string, filter: { key: string; value: string; }[]): HttpParams {
    let queryParameters = new HttpParams();
    if (pageIndex) {
      queryParameters = queryParameters.set('pageIndex', pageIndex);
    }
    if (pageSize) {
      queryParameters = queryParameters.set('pageSize', pageSize);
    }
    if (sortField) {
      queryParameters = queryParameters.set('ordering', sortField);
    }
    if (sortOrder) {
      queryParameters = queryParameters.set('ordering_direction', sortOrder);
    }
    if (searchValue) {
      queryParameters = queryParameters.set(searchField, searchValue);
    }
    if (filter) {
      const parameter_name = filter[0].key;
      const filter_value = filter[0].value;
      if (filter_value) {
        queryParameters = queryParameters.set(parameter_name, filter_value);
      }
    }
    return queryParameters;
  }

  getRequests(pageIndex: number, pageSize: number, sortField: string | null, sortOrder: string | null, filter: Array<{ key: string; value: string}> | null, searchField: string, searchValue: string ): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.Requests}`;
    const queryParameters  = this.generateQueryParametersEidup(pageIndex, pageSize, sortField, sortOrder, searchField, searchValue, filter);

    return this._handleCmsRequest<GenericResponse>(
      this.http.get<GenericResponse>(url, { params: queryParameters, withCredentials: true }),
      (response: GenericResponse) => { this.logger.log('Company requests retrieved'); }
    );
  }

  getDrafts(pageIndex: number, pageSize: number, sortField: string | null, sortOrder: string | null, filter: Array<{ key: string; value: string}> | null, searchField: string, searchValue: string ): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.Drafts}`;
    const queryParameters  = this.generateQueryParametersEidup(pageIndex, pageSize, sortField, sortOrder, searchField, searchValue, filter);

    return this._handleCmsRequest<GenericResponse>(
      this.http.get<GenericResponse>(url, { params: queryParameters, withCredentials: true }),
      (response: GenericResponse) => { this.logger.log('Company Drafts retrieved'); }
    );
  }

  cancelRequest(requestId: number): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.CancelRequest}/${requestId}`;

    return this._handleCmsRequest<GenericResponse>(
      this.http.delete<GenericResponse>(url, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta cancellata con successo',
          ''
        );
      }
    );
  }

  uploadDocument(requestId: number, typeDocument: string, sideDocument: string, documentData: string, formatDocument: string, name: string) {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.AddDocument}`;
    const payload = {
      requestId,
      name,
      type: typeDocument,
      format: formatDocument,
      side: sideDocument,
      data: documentData,
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Documento caricato con successo',
          ''
        );
      }
    );
  }

  approveRequest(payload: any): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.ApproveRequest}`;

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta approvata con successo',
          ''
        );
      }
    );
  }


  enrollRequest(token: string, controlCode: string, otp: number): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.EnrollRequest}`;
    const payload = {
      otp,
      token,
      controlCode
    };

    return this._handleCmsRequest<GenericResponse>(
      this.http.post<GenericResponse>(url, payload, { withCredentials: true }),
      (response: GenericResponse) => {
        this.notificationService.createNotification(
          NotificationType.SUCCESS,
          'Richiesta di attivazione inviata con successo',
          'Attendi l\'attivazione dell\'identità SPID per l\'utenza richiesta.'
        );
      }
    );
  }

  getAccessLogs(page: number): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.GetAccessLogs}`;
    const queryParameters = new HttpParams();
    queryParameters.set('page', page.toString());

    return this._handleCmsRequest<GenericResponse>(
      this.http.get<GenericResponse>(url, { params: queryParameters, withCredentials: true }),
      (response: GenericResponse) => { this.logger.log('Logs downloaded'); }
    );
  }

  getContract(): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.GetContract}`;
    const queryParameters = new HttpParams();

    return this._handleCmsRequest<GenericResponse>(
      this.http.get<GenericResponse>(url, { params: queryParameters, withCredentials: true }),
      (response: GenericResponse) => { this.logger.log('Logs downloaded'); }
    );
  }

  getEidupContract(requestId: number): any {
    const url = `${ConfigService.settings.baseUrl}/${ApiPaths.DownloadEidupContract}`;
    return `${url}?requestId=${requestId}`;
  }

  private showQrCodeAssociationModal(identities: Identity[]): boolean {
    return (identities.map(id => id?.otp_delivery_methods?.APP) || []).filter(app => app.status !== OtpDeliveryMethodStatus.Enrolled && app.show_login_popup).length !== 0;
  }

  private isThereALevel2Identity(identities: Identity[]): boolean {
    return identities.filter(id => +id.level === 2).length !== 0;
  }

  private isThereAnIssuedIdentity(identities: Identity[]): boolean {
    return identities.filter(id => id.status === 'ISSUED').length !== 0;
  }

  registerOnQrCodeModalVisibilityChange(): void {
    this.authService.currentUserChanged.pipe(
      takeUntil(this._qrCodeModalClosed$.pipe(filter(value => value))),
      skipWhile(user => !user),
      tap(user => {
        const identities = user.identities;
        this._isQrCodeModalVisible$.next(this.showQrCodeAssociationModal(identities) && this.isThereALevel2Identity(identities) && this.isThereAnIssuedIdentity(identities));
      }),
      finalize(() => this._isQrCodeModalVisible$.next(false))
    ).subscribe();
  }
}
